import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, GraduationCap } from "lucide-react";
import { resolveHostSchoolPromoView } from "../../lib/hostSchoolPromoView";
import { eventEditPath } from "../../lib/eventLinks";
import LinkifiedText from "../LinkifiedText";

/**
 * Host-school band under the event header.
 * Guests: only the host's school from event creation (rsvp.dance or external link).
 * Host without a school (not opted out): setup prompt — never shown to guests.
 */
export default function HostSchoolPromo({
  hostSchool,
  hostSchoolOptOut = false,
  isHosting = false,
  event = null,
  eventName = null,
  className = "",
}) {
  const view = resolveHostSchoolPromoView({
    hostSchool,
    hostSchoolOptOut,
    isHosting,
  });

  if (view.type === "hidden") return null;

  if (view.type === "promo") {
    const blurb =
      hostSchool.blurb
      || (hostSchool.city ? `Classes in ${hostSchool.city}` : "See the hosts’ classes");
    const ctaClass =
      "host-secondary-btn inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded-btn bg-surface-muted text-ink border border-subtle px-4 py-2.5 font-ui font-semibold text-[14px] shrink-0 hover:border-[color:var(--host-cta)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 transition-colors";
    const ctaLabel = "See classes";

    const cta = view.isExternal ? (
      <a
        href={view.href}
        target="_blank"
        rel="noopener noreferrer"
        className={ctaClass}
        data-testid="host-school-promo-cta"
      >
        {ctaLabel}
        <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </a>
    ) : (
      <Link to={view.href} className={ctaClass} data-testid="host-school-promo-cta">
        {ctaLabel}
      </Link>
    );

    const title = eventName ? (
      <p className="font-ui font-semibold text-[16px] text-ink leading-snug text-pretty">
        Hosted by {view.name}
      </p>
    ) : (
      <p className="font-ui font-semibold text-[16px] text-ink leading-snug text-pretty">
        Keep dancing with {view.name}
      </p>
    );

    return (
      <section
        className={`quiet-card px-4 py-4 ${className}`}
        data-testid="host-school-promo"
        aria-label={`Host school: ${view.name}`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-full bg-canvas text-ink/70 border border-subtle flex items-center justify-center font-ui font-medium text-[12px] shrink-0"
              aria-hidden
            >
              {view.initials}
            </div>
            <div className="min-w-0">
              {title}
              <p className="mt-0.5 font-ui text-[13px] font-normal text-ink/55 leading-snug whitespace-pre-line">
                <LinkifiedText>{blurb}</LinkifiedText>
              </p>
            </div>
          </div>
          {cta}
        </div>
      </section>
    );
  }

  // Defense in depth: setup is host-only even if a caller forgets isHosting.
  if (!isHosting) return null;

  const editHref = event?.id ? `${eventEditPath(event)}#host-school` : "/create";

  return (
    <section
      className={`rounded-card border border-dashed border-ink/20 bg-surface-muted px-4 py-4 ${className}`}
      data-testid="host-school-promo-setup"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-full bg-orchid/40 text-ink flex items-center justify-center shrink-0" aria-hidden>
            <GraduationCap className="w-5 h-5" strokeWidth={2.25} />
          </div>
          <div className="min-w-0">
            <p className="font-ui font-semibold text-[15px] text-ink leading-snug">
              Add your school so dancers can find your classes
            </p>
            <p className="mt-0.5 font-ui text-[13px] font-medium text-ink/70 leading-snug">
              Link your rsvp.dance school or website — guests will see it here under the event header.
            </p>
          </div>
        </div>
        <Link
          to={editHref}
          className="inline-flex items-center justify-center min-h-[44px] rounded-btn bg-ink text-cream px-4 py-2.5 font-ui font-medium text-[14px] shrink-0"
          data-testid="host-school-promo-setup-cta"
        >
          Set up school
        </Link>
      </div>
    </section>
  );
}
